import React, { useState, useEffect } from "react";
import SharesDataService from "../services/SharesService";
import UserDataService from "../services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  FormControlLabel,
  Switch
} from "@mui/material";
import SmallCalculator from "../../components/SmallCalculator";
import CalculateIcon from "@mui/icons-material/Calculate";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import StampDutyService from "../services/StampDutyService";
import SplitDepositModal from "../../components/SplitDepositModal";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AddShares = () => {
  const { owner, id } = useParams(); // accept optional :id for edit
  const navigate = useNavigate();

  const initialSharesState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
    newBalance: null
  };

  const [shares, setShares] = useState(initialSharesState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [showCalc, setShowCalc] = useState(false);
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState("50");
  const [waiveReason, setWaiveReason] = useState("");
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const isEdit = Boolean(id);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setShares(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    SharesDataService.get(id)
      .then(res => {
        let src = res?.data ?? res ?? {};
        if (Array.isArray(src)) src = src.find(item => (item._id || item.id) === id) || src[0] || {};
        // unwrap single-key wrapper
        if (!src || Object.keys(src).length === 1) {
          const key = Object.keys(src)[0];
          const val = key ? src[key] : undefined;
          if (val && (val._id || val.id)) src = val;
        }

        const ownerVal = src?.owner;
        const ownerId = ownerVal && typeof ownerVal === "object"
          ? (ownerVal._id || ownerVal.id || String(ownerVal))
          : ownerVal;
        if (ownerVal && typeof ownerVal === "object") {
          const fname = ownerVal.firstName || ownerVal.firstname || ownerVal.first_name || ownerVal.name || "";
        const lname = ownerVal.lastName || ownerVal.lastname || ownerVal.last_name || "";
        setOwnerDisplay((lname ? lname + (fname ? " " : "") : "") + fname);
        }

        setShares(prev => ({
          ...prev,
          owner: ownerId ?? initialSharesState.owner,
          date: src?.date ?? initialSharesState.date,
          amount: (src?.amount != null) ? String(src.amount) : "",
          creditOrDebit: src?.creditOrDebit ?? prev.creditOrDebit,
          description: src?.description ?? prev.description,
          createdAt: src?.createdAt ?? prev.createdAt,
          createdBy: src?.createdBy ?? prev.createdBy,
          updatedAt: src?.updatedAt ?? prev.updatedAt,
          updatedBy: src?.updatedBy ?? prev.updatedBy,
          newBalance: src?.newBalance ?? prev.newBalance,
        }));
      })
      .catch((e) => {
        console.error("load shares:", e, e?.response?.data);
        setError(e?.response?.data?.message || e?.message || "Could not load record");
      })
      .finally(() => setLoading(false));
    }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const saveShares = async () => {
    setError("");
    const parsedAmount = parseNum(shares.amount);
    if (!shares.amount || parsedAmount <= 0 || Number.isNaN(parsedAmount)) {
      setError("Please enter a valid amount");
      return;
    }
    if (!shares.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: shares.owner,
        date: typeof shares.date === "string" ? shares.date : toDateInput(shares.date),
        amount: parsedAmount,
        creditOrDebit: String(shares.creditOrDebit),
        description: (shares.description || "").trim()
        // do NOT send createdAt/createdBy from client — backend should set them
      };

      let res;
      if (isEdit) {
        res = await SharesDataService.update(id, payload);
      } else {
        res = await SharesDataService.create(shares.owner, payload);
      }
      const r = res?.data || {};
      const ownerId = (r.owner && typeof r.owner === "object") ? (r.owner._id || r.owner.id || String(r.owner)) : (r.owner ?? shares.owner);

      // Record Stamp Duty or waiver for deposit
      if (shares.creditOrDebit === "credit" && !isEdit) {
        const dutyNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;
        StampDutyService.record({
          memberId: ownerId,
          depositAmount: payload.amount,
          stampDutyAmount: dutyNum,
          accountType: "shares",
          isWaived: !applyStampDuty,
          waiveReason: !applyStampDuty ? (waiveReason || "Waived on Shares deposit") : "",
          date: payload.date,
          notes: payload.description || `Shares deposit (Stamp Duty: ₦${dutyNum})`
        }).catch(err => console.error("Stamp duty recording error:", err));
      }

      // update local state with server-returned metadata (created/updated/newBalance)
      setShares({
        owner: (r.owner && typeof r.owner === "object") ? (r.owner._id || r.owner.id || String(r.owner)) : (r.owner ?? shares.owner),
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null,
        newBalance: r.newBalance ?? null
      });

      setSubmitted(true);
      setSnackOpen(true);

      navigate(`/dashboard/shares/${encodeURIComponent(ownerId)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newShares = () => {
    setShares({ ...initialSharesState, owner: owner || initialSharesState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add Shares Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newShares}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={shares.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={shares.amount}
                onChange={(e) => {
                  const val = formatInputWithCommas(e.target.value);
                  setShares(prev => ({ ...prev, amount: val }));
                }}
                onBlur={() => {
                  if (shares.amount) {
                    setShares(prev => ({ ...prev, amount: formatToCurrencyString(shares.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
              <Button
                startIcon={<CalculateIcon />}
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setShowCalc(true)}
              >
                Calculator
              </Button>
              <Dialog open={showCalc} keepMounted onClose={() => setShowCalc(false)} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                  <SmallCalculator
                    initialAmount={shares.amount}
                    onApplyShares={(val) => {
                      const v = (val == null) ? "" : formatToCurrencyString(val);
                      setShares(prev => ({ ...prev, amount: v }));
                      setShowCalc(false);
                    }}
                    onCopySavings={() => { /* optional hook */ }}
                    onClose={() => setShowCalc(false)}
                  />
                </DialogContent>
              </Dialog>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={shares.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setShares(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={shares.creditOrDebit === "debit" ? "contained" : "outlined"}
                  onClick={() => setShares(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>
            {/* STAMP DUTY CONTROL FOR DEPOSIT */}
            {shares.creditOrDebit === "credit" && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0"
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                        Stamp Duty (EMTL)
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={applyStampDuty}
                          onChange={(e) => setApplyStampDuty(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50} Stamp Duty` : "Waive Stamp Duty"}
                      sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                    />
                  </Box>

                  {applyStampDuty ? (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                        Duty Amount:
                      </Typography>
                      {[50, 100, 200].map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                          onClick={() => setStampDutyAmount(String(preset))}
                          sx={{
                            minWidth: 64,
                            py: 0.3,
                            px: 1,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                            color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                            borderColor: "#0D9488"
                          }}
                        >
                          ₦{preset} {preset === 50 ? "(Std)" : ""}
                        </Button>
                      ))}
                      <TextField
                        size="small"
                        type="number"
                        label="Custom Duty (₦)"
                        value={stampDutyAmount}
                        onChange={(e) => setStampDutyAmount(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 140, backgroundColor: "#ffffff" }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Reason for waiving (e.g. Single bulk transfer split across savings/shares/loan)"
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                    />
                  )}

                  <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      Is this part of a single bank deposit shared between Savings, Shares, and Loan?
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSplitModalOpen(true)}
                      startIcon={<CallSplitRoundedIcon />}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "6px", fontSize: "0.75rem", borderColor: "#0D9488", color: "#0D9488" }}
                    >
                      Single Deposit Split Tool (1x Stamp Duty)
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={shares.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveShares}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newShares} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>

      {/* SINGLE DEPOSIT SPLIT MODAL */}
      <SplitDepositModal
        open={splitModalOpen}
        onClose={() => setSplitModalOpen(false)}
        defaultMemberId={shares.owner}
        onSuccess={() => {
          setSnackOpen(true);
          const ownerId = shares.owner;
          if (ownerId) {
            navigate(`/dashboard/shares/${encodeURIComponent(ownerId)}`);
          }
        }}
      />
    </Paper>
  );
};

export default AddShares;
